import React, {useCallback, useEffect, useMemo, useState} from "react";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import DateUtils from "../../../../core/services/date-utils";
import Form, {MuiRFF, useFormV2, Yup} from '../../form';
import useIndustry from "../../../hooks/use-industry";
import {Box, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Popover, Typography} from "@mui/material";
import UIUtils from "../../../utils";
import useCachedPriceLevelsDictionaryController from "../../../hooks/use-price-level-dictionary";
import {useParams} from "react-router-dom";
import Grid2 from "@mui/material/Unstable_Grid2";
import PartAutocomplete from "../../text-fields/autocomplete/part-autocomplete";
import {Alert} from "@mui/lab";
import Chips, {ChipsContentType, ChipsIconPosition, ChipsTypes} from "../../base/chips/chips";


export const PriceMatrixSources = {
    cost: {id: 1, title: 'Cost'},
    finalPrice: {id: 2, title: 'Final Price'},
}

export const PriceMatrixTypes = {
    part: {id: 1, title: 'Part'},
    priceLevel: {id: 2, title: 'Price Level'},
    category: {id: 3, title: 'Category'},
    general: {id: 4, title: 'Whole system'},
}

export const PriceMatrixValueTypes = {
    price: {id: 1, title: '$'},
    percent: {id: 2, title: '%'},
}

export const PriceMatrixModifierTypes = {
    increase: {id: 1, title: 'Increase'},
    decrease: {id: 2, title: 'Decrease'},
    set: {id: 3, title: 'Set'},
}

const valueTypes = [PriceMatrixValueTypes.price, PriceMatrixValueTypes.percent];
const modifierTypes = [PriceMatrixModifierTypes.increase, PriceMatrixModifierTypes.decrease, PriceMatrixModifierTypes.set];
const sources = [PriceMatrixSources.finalPrice];
const types = [PriceMatrixTypes.part, PriceMatrixTypes.category, PriceMatrixTypes.priceLevel, PriceMatrixTypes.general];

const UpsertPriceMatrixDialog = ({open, close, onSave, matrix}) => {
    const params = useParams();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const isMounted = useIsMounted();
    const [isCreate, setIsCreate] = useState(!!matrix);

    /**
     * With each open of the dialog, sets the isCreate state based on the presence of the matrix prop.
     */
    useEffect(() => {
        if (open) {
            setIsCreate(!matrix);
            setErrorMessage('');
        }
    }, [open, matrix]);


    const schema = useMemo(() => {
        return Yup.object().shape({
            modifier: Yup.mixed().nullable().required(ValidateMessages.required),
            basedOn: Yup.mixed().when('modifier', {
                is: (modifier) => modifier?.id !== PriceMatrixModifierTypes.set.id,
                then: Yup.mixed().nullable().required(ValidateMessages.required),
                otherwise: Yup.mixed().nullable(),
            }),
            type: Yup.mixed().nullable().required(ValidateMessages.required),
            target: Yup.mixed().when('type', {
                is: (type) => type?.id === PriceMatrixTypes.general.id,
                then: Yup.mixed().nullable(),
                otherwise: isCreate
                    ? Yup.array().of(Yup.mixed()).min(1, ValidateMessages.required).required(ValidateMessages.required)
                    : Yup.mixed().nullable().required(ValidateMessages?.required),
            }),
            value: Yup.string().nullable().required(ValidateMessages.required),
            valueType: Yup.mixed().when('modifier', {
                is: (modifier) => modifier?.id !== PriceMatrixModifierTypes.set.id,
                then: Yup.mixed().nullable().required(ValidateMessages.required),
                otherwise: Yup.mixed().nullable(),
            }),
            startDate: Yup.mixed()
                .validDate(ValidateMessages.incorrect)
                .maxDate(Yup.ref('endDate'), ValidateMessages.lessThan("End Date"))
                .nullable().required(ValidateMessages.required),
            endDate: Yup.mixed()
                .validDate(ValidateMessages.incorrect)
                .disablePastDate(ValidateMessages.minDate("today"), 'day')
                .minDate(Yup.ref('startDate'), ValidateMessages.greaterThan("Start Date"))
                .nullable(),
        }, [
            ['startDate', 'endDate'],
            ['endDate', 'startDate'],
            ['target', 'type'],
            ["basedOn", 'modifier'],
            ['valueType', 'modifier'],
        ])
    }, [isCreate]);

    const initialValues = useMemo(() => {
        if (!!matrix) {
            return {
                type: matrix.part !== null ? PriceMatrixTypes.part : matrix.partCategory !== null ? PriceMatrixTypes.category : matrix.priceLevel !== null ? PriceMatrixTypes.priceLevel : PriceMatrixTypes.general,
                target: matrix.part ?? matrix.partCategory ?? matrix.priceLevel,
                value: Math.abs(parseFloat(matrix.value)).toString(),
                modifier: matrix.isFixedValue
                    ? PriceMatrixModifierTypes.set
                    : matrix.value < 0
                        ? PriceMatrixModifierTypes.decrease
                        : PriceMatrixModifierTypes.increase,
                basedOn: matrix.isBasedOnCost ? PriceMatrixSources.cost : PriceMatrixSources.finalPrice,
                valueType: matrix.isFixedValue
                    ? null
                    : matrix.isPercentage
                        ? PriceMatrixValueTypes.percent
                        : PriceMatrixValueTypes.price,
                startDate: matrix.startDate ? DateUtils.serverToUTC(matrix.startDate) : null,
                endDate: matrix.endDate ? DateUtils.serverToUTC(matrix.endDate) : null,
            }
        } else {
            return {}
        }
    }, [matrix])

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Prepares the form values to be sent to add / edit apis for price matrices.
     * @param values {any} form values
     */
    const onSubmit = useCallback(async (values, form) => {
        setErrorMessage('');
        const targets = values.type.id === PriceMatrixTypes.general.id ? [null] : isCreate ? values.target : [values.target];
        const requests = targets.map(target => ({
            target: target,
            part: values.type.id === PriceMatrixTypes.part.id ? target : null,
            partCategory: values.type.id === PriceMatrixTypes.category.id ? target : null,
            priceLevel: values.type.id === PriceMatrixTypes.priceLevel.id ? target : null,
            value: values.modifier?.id === PriceMatrixModifierTypes.decrease.id ? -parseFloat(values.value) : parseFloat(values.value),
            isFixedValue: values.modifier?.id === PriceMatrixModifierTypes.set.id,
            isPercentage: values.modifier?.id === PriceMatrixModifierTypes.set.id ? false : values.valueType.id === PriceMatrixValueTypes.percent.id,
            isBasedOnCost: values.basedOn?.id === PriceMatrixSources.cost.id,
            startDate: values.startDate ? DateUtils.UTCToServer(values.startDate).format(DateUtils.withoutOffsetFormat) : undefined,
            endDate: values.endDate ? DateUtils.UTCToServer(values.endDate).format(DateUtils.withoutOffsetFormat) : undefined,
            customer: params.customerId ? {id: parseInt(params.customerId)} : undefined,
            id: matrix ? matrix.id : undefined,
        }));

        if (!!matrix) {
            const forApi = requests[0];
            setLoading(true);
            const response = await Api.updatePriceMatrix(forApi);
            if (!isMounted())
                return;
            setLoading(false);
            if (response?.resultFlag) {
                onSave?.(forApi);
                close(true);
            }
        } else {
            setLoading(true);
            const responses = await Promise.all(requests.map(request => Api.createPriceMatrix(request)));
            if (!isMounted())
                return;
            setLoading(false);
            const successfulMatrices = [];
            const unsuccessfulMatrices = [];
            for (const response of responses) {
                const index = responses.indexOf(response);
                if (response?.resultFlag) {
                    successfulMatrices.push(response?.data);
                } else {
                    unsuccessfulMatrices.push(requests[index]);
                }
            }
            if (successfulMatrices.length > 0) {
                onSave?.(successfulMatrices);
            }
            if (successfulMatrices.length > 0 && unsuccessfulMatrices.length === 0) {
                close(true);
            } else {
                if (values.type.id !== PriceMatrixTypes.general.id) {
                    setErrorMessage('Some targets were not added due to the provided errors via the notification on the top right corner. Please review and try again.');
                    const unsuccessfulTargets = unsuccessfulMatrices.map(e => e.target);
                    setTimeout(() => {
                        form.change('target', unsuccessfulTargets);
                    }, 300)
                }
            }
        }
    }, [close, isCreate, isMounted, matrix, onSave, params.customerId])


    return (
        <Dialog
            open={open}
            loading={loading}
            disableEscapeKeyDown={loading}
            disableBackdropClick={loading}
            setOpen={close}
            maxWidth={'sm'}
            scroll={'paper'}
            className={'bound-dialog'}
        >
            <DialogTitle component={'div'}>
                <Typography variant={'h6'} sx={{paddingTop: 2, paddingBottom: 2, paddingLeft: 2, paddingRight: 2,}}>
                    {
                        matrix
                            ? params.customerId
                                ? 'Edit Tier Pricing'
                                : 'Edit Price Matrix'
                            : params.customerId
                                ? 'Add Tier Pricing'
                                : 'Add Price Matrix'
                    }
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{marginTop: 1, padding: 3}}>
                    <Form onSubmit={onSubmit} validate={validate} formProps={{id: 'upsert-price-matrix-form'}} initialValues={initialValues}>
                        {({values, form, errors}) => {
                            if (values.basedOn?.id === PriceMatrixSources.cost.id) {
                                form.change('basedOn', PriceMatrixSources.finalPrice);
                            }
                            return (
                                <Grid2 container spacing={4} sx={{marginBlockEnd: 3}}>
                                    <Grid2 xs={12}>
                                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                            <Typography variant="body2" color={'tertiary.main'}>
                                                Modifications
                                            </Typography>
                                            <Grid2 container spacing={2}>
                                                <Grid2 xs={6}>
                                                    <MuiRFF.TextField
                                                        select
                                                        name="modifier"
                                                        label="Modifier"
                                                        required={required.modifier}
                                                        fullWidth
                                                    >
                                                        {
                                                            modifierTypes?.map(e => (
                                                                <MenuItem key={e.id} value={e}>{e.title}</MenuItem>
                                                            ))
                                                        }
                                                    </MuiRFF.TextField>
                                                </Grid2>
                                                {
                                                    values.modifier?.id !== PriceMatrixModifierTypes.set.id &&
                                                    <Grid2 xs={12} md={6}>
                                                        <MuiRFF.TextField
                                                            select
                                                            name="basedOn"
                                                            label="Source"
                                                            required={required.basedOn}
                                                            fullWidth
                                                        >
                                                            {
                                                                sources?.map(e => (
                                                                    <MenuItem key={e.id} value={e}>{e.title}</MenuItem>
                                                                ))
                                                            }
                                                        </MuiRFF.TextField>
                                                    </Grid2>
                                                }
                                                <Grid2 xs={values.modifier?.id !== PriceMatrixModifierTypes.set.id ? 8 : 6}>
                                                    <MuiRFF.TextField
                                                        name="value"
                                                        label="Value"
                                                        type="number"
                                                        required={required.value}
                                                        fullWidth
                                                        InputProps={{inputProps: {min: 0}}}
                                                    />
                                                </Grid2>
                                                {
                                                    values.modifier?.id !== PriceMatrixModifierTypes.set.id &&
                                                    <Grid2 xs={12} md={4}>
                                                        <MuiRFF.TextField
                                                            select
                                                            name="valueType"
                                                            label="Unit"
                                                            required={required.valueType}
                                                            fullWidth
                                                        >
                                                            {
                                                                valueTypes?.map(e => (
                                                                    <MenuItem key={e.id} value={e}>{e.title}</MenuItem>
                                                                ))
                                                            }
                                                        </MuiRFF.TextField>
                                                    </Grid2>
                                                }
                                            </Grid2>
                                        </Box>
                                    </Grid2>
                                    <Grid2 xs={12}>
                                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                            <Typography variant="body2" color="tertiary.main">
                                                Targeting
                                            </Typography>
                                            <Grid2 container spacing={2}>
                                                <Grid2 xs={12}>
                                                    <MuiRFF.TextField
                                                        select
                                                        name="type"
                                                        label="Type"
                                                        required={required.type}
                                                        fullWidth
                                                        onChange={(e) => {
                                                            form.change('type', e.target.value);
                                                            form.change('target', isCreate ? [] : null);
                                                        }}
                                                        SelectProps={{
                                                            renderValue: (selected) => selected?.title ?? '',
                                                        }}
                                                    >
                                                        {
                                                            types?.map(e => (
                                                                <MenuItem key={e.id} value={e}>{e.title}</MenuItem>
                                                            ))
                                                        }
                                                    </MuiRFF.TextField>
                                                </Grid2>
                                                {
                                                    !!values.type?.id &&
                                                    <Grid2 xs={12}>
                                                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                                            {
                                                                values.type?.id !== PriceMatrixTypes.general.id && isCreate &&
                                                                <Alert variant={'outlined'} severity={!!errorMessage ? 'warning' : 'info'}
                                                                       sx={{borderColor: !!errorMessage ? 'warning.main' : 'info.main'}}>
                                                                    {errorMessage || "You can add multiple targets at once by selecting more than one option."}
                                                                </Alert>
                                                            }
                                                            {
                                                                values.type?.id === PriceMatrixTypes.part.id &&
                                                                <PartSelection
                                                                    required={required}
                                                                    isCreate={isCreate}
                                                                    loading={loading}
                                                                />
                                                            }
                                                            {
                                                                values.type?.id === PriceMatrixTypes.category.id &&
                                                                <CategorySelection
                                                                    required={required}
                                                                    isCreate={isCreate}
                                                                    loading={loading}
                                                                />
                                                            }
                                                            {
                                                                values.type?.id === PriceMatrixTypes.priceLevel.id &&
                                                                <PriceLevelSelection
                                                                    required={required}
                                                                    isCreate={isCreate}
                                                                    loading={loading}
                                                                />
                                                            }
                                                        </Box>
                                                    </Grid2>
                                                }
                                            </Grid2>
                                        </Box>
                                    </Grid2>
                                    <Grid2 xs={12}>
                                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                            <Typography variant="body2" color="tertiary.main">
                                                Duration
                                            </Typography>
                                            <Grid2 container spacing={2}>
                                                <Grid2 xs={12} md={6}>
                                                    <MuiRFF.DatePicker
                                                        name="startDate"
                                                        label="Start Date"
                                                        required={required.startDate}
                                                        maxDate={values.endDate}
                                                        fullWidth
                                                    />
                                                </Grid2>
                                                <Grid2 xs={12} md={6}>
                                                    <MuiRFF.DatePicker
                                                        name="endDate"
                                                        label="End Date"
                                                        required={required.endDate}
                                                        minDate={values.startDate}
                                                        disablePast
                                                        fullWidth
                                                    />
                                                </Grid2>
                                            </Grid2>
                                        </Box>
                                    </Grid2>
                                </Grid2>
                            );
                        }}
                    </Form>
                </Box>
            </DialogContent>
            <DialogActions className={'p-0'}>
                <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <Button
                        disabled={loading}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => close()}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                    <Button
                        disabled={loading}
                        variant={'dialog'}
                        color={'primary'}
                        type={'submit'}
                        form={'upsert-price-matrix-form'}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {loading ? "loading..." : !isCreate ? 'Save' : 'Add'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}


const PartSelection = ({required, isCreate, loading}) => {
    const {form, values, submitting} = useFormV2();

    // noinspection JSUnusedGlobalSymbols
    return (
        <>
            <PartAutocomplete
                form
                name="target"
                label="Target"
                required={required.target}
                multiple={isCreate}
                disabled={loading || submitting}
                disableClearable
                fullWidth
                {...(isCreate && {
                    renderTags: () => "",
                })}
            />
            {
                isCreate &&
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                    {
                        Array.isArray(values.target) && values.target.map((part) => (
                            <Chips
                                key={part.id}
                                disabled={loading || submitting}
                                contentType={ChipsContentType.text}
                                iconPosition={ChipsIconPosition.end}
                                type={ChipsTypes.remove}
                                onIconClick={() => form.change('target', values.target.filter(e => e.id !== part.id))}
                                content={part.partNo}
                                className={'pl-3'}
                            />
                        ))
                    }
                </Box>
            }
        </>
    );

}


const CategorySelection = ({required, loading, isCreate}) => {
    const {values, form, submitting} = useFormV2();
    const {industries, fetchedIndustry, fetchIndustry, categoriesOfFetchedIndustry, cachedIndustry, hasNoIndustries} = useIndustry();
    const [industriesPopoverAnchor, setIndustriesPopoverAnchor] = useState(null);
    const isMounted = useIsMounted();


    const selectedIndustry = useMemo(() => fetchedIndustry ?? cachedIndustry, [fetchedIndustry, cachedIndustry])
    const categories = useMemo(() => categoriesOfFetchedIndustry?.filter(e => !e.parentId).map(e => ({...e, industry: selectedIndustry})), [categoriesOfFetchedIndustry, selectedIndustry])

    /**
     * With each change in the selected industry, fetches the list of categories of that industry.
     */
    useEffect(() => {
        if (cachedIndustry?.id) {
            void fetchIndustry({industryId: cachedIndustry.id})
        }
    }, [cachedIndustry, fetchIndustry]);

    /**
     * Automatically fetches the industry if there is only one industry available and it's not already fetched or selected
     */
    useEffect(() => {
        if (industries?.length === 1) {
            const industry = industries[0];
            void fetchIndustry({industryId: industry.id});
        }
    }, [fetchIndustry, industries]);


    /**
     * Fetches the industry of the selected category and sets it as the selected industry.
     */
    const findIndustryOfSelectedCategory = useCallback(async (categoryId) => {
        if (!categoryId)
            return;
        const response = await Api.getCategoryIndustryIds({ids: [categoryId]});
        if (!isMounted())
            return;
        const industryId = response.data.find(e => e.id === categoryId)?.industryId
        if (industryId) {
            void fetchIndustry({industryId: industryId})
        }
    }, [isMounted, fetchIndustry])

    /**
     * With each change in the type of the selected values:
     * - if type is category and we have  selected category, we fetch the industry of that category.
     */
    useEffect(() => {
        if (values?.type?.id === PriceMatrixTypes.category.id && !!values?.target?.id) {
            void findIndustryOfSelectedCategory(values.target?.id);
        }
    }, [values?.type?.id, values.target?.id, findIndustryOfSelectedCategory]);

    if (hasNoIndustries) {
        return (
            <Typography variant={'body2'} className={'grey-3 text-center'}>
                No industries found. please contact your administrator or manage your industries in the settings panel.
            </Typography>
        )
    }

    return (
        <>
            <MuiRFF.TextField
                select
                name="target"
                label="Target"
                required={!!required.target}
                fullWidth
                disabled={loading || submitting}
                SelectProps={{
                    multiple: isCreate,
                    renderValue: (selected) => !isCreate ? selected?.title ?? '' : ''
                }}
                InputProps={{
                    startAdornment:
                        selectedIndustry?.id &&
                        values?.type?.id === PriceMatrixTypes.category.id
                            ? (
                                <IconButton
                                    color={"secondary"}
                                    sx={{marginInlineEnd: 0.5, cursor: industries?.length > 1 ? 'pointer' : 'default !important'}}
                                    onClickCapture={(e) => industries?.length > 1 ? setIndustriesPopoverAnchor(e.currentTarget) : void 0}
                                >
                                    {React.cloneElement(UIUtils.getIndustryIcon(selectedIndustry?.iconFileName) ?? <div/>, {style: {width: 24, height: 24},})}
                                </IconButton>
                            ) : null,
                }}
            >
                {
                    categories.map(e => (
                        <MenuItem key={e.id} value={e}>{e.title}</MenuItem>
                    ))
                }
            </MuiRFF.TextField>
            {
                isCreate &&
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                    {
                        Array.isArray(values.target) && values.target.map((category) => (
                            <Chips
                                key={category.id}
                                disabled={loading || submitting}
                                contentType={ChipsContentType.element}
                                iconPosition={ChipsIconPosition.end}
                                type={ChipsTypes.remove}
                                onIconClick={() => form.change('target', values.target.filter(e => e.id !== category.id))}
                                content={
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, marginInlineStart: 1}}>
                                        {
                                            category?.industry &&
                                            React.cloneElement(UIUtils.getIndustryIcon(category?.industry?.iconFileName) ?? <div/>, {style: {width: 16, height: 16, minWidth: 16}, className: 'fill'})
                                        }
                                        <Typography variant={'caption'}>
                                            {category?.title ?? ""}
                                        </Typography>
                                    </Box>
                                }
                            />
                        ))
                    }
                </Box>
            }
            <Popover
                open={!!industriesPopoverAnchor}
                onClose={() => setIndustriesPopoverAnchor(null)}
                anchorEl={industriesPopoverAnchor}
                elevation={1}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'center'}}
            >
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 200, padding: 1,}}>
                    <Typography variant={'body2'} fontWeight={600} sx={{marginBlockEnd: 1}}>
                        Select your Industry
                    </Typography>
                    {
                        industries.map((e) => (
                            <MenuItem
                                key={e.id}
                                value={e.id}
                                selected={(selectedIndustry)?.id === e.id}
                                onClick={() => {
                                    setIndustriesPopoverAnchor(null);
                                    if (!isCreate) {
                                        form.change('target', null);
                                    } else {
                                        // just remove the text in the input of the autocomplete.

                                    }
                                    void fetchIndustry({industryId: e.id})
                                }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    width: 'fill-available',
                                    borderRadius: 1,
                                    marginInline: 0,
                                    padding: 1,
                                    '.fill': {
                                        color: 'primary.main',
                                    }
                                }}
                            >
                                {React.cloneElement(UIUtils.getIndustryIcon(e?.iconFileName) ?? <div/>, {style: {width: 24, height: 24, minWidth: 24},})}
                                <Typography variant={'caption'}>
                                    {e.title ?? ""}
                                </Typography>
                            </MenuItem>
                        ))
                    }
                </Box>
            </Popover>
        </>
    )
}


const PriceLevelSelection = ({required, loading, isCreate}) => {
    const {values, form, submitting} = useFormV2();
    const [loadingPriceLevels, setLoadingPriceLevels] = useState(false);
    const [priceLevels, setPriceLevels] = useState([]);
    const isMounted = useIsMounted();
    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController()

    /**
     * Fetches the list of price levels form the server and sets the list.
     */
    const getPriceLevels = useCallback(async () => {
        setLoadingPriceLevels(true);
        const [priceLevelDict, priceLevelsResponse] = await Promise.all([
            getPriceLevelDictionary(),
            Api.getAllPriceLevels()
        ])
        if (!isMounted())
            return;
        if (priceLevelsResponse?.data) {
            const dict = priceLevelDict ?? [];
            setPriceLevels(priceLevelsResponse?.data?.map(e => ({
                ...e,
                displayTitle: dict?.find(d => d.priceLevelId === e.id)?.title ?? e.title
            })) ?? []);
        }
        setLoadingPriceLevels(false);
    }, [getPriceLevelDictionary, isMounted])

    /**
     * As soon as the component mounts, fetches the list of price levels from the server.
     */
    useEffect(() => {
        void getPriceLevels();
    }, [getPriceLevels])

    return (
        <>
            <MuiRFF.TextField
                select
                name="target"
                label="Target"
                required={!!required.target}
                fullWidth
                disabled={loading || submitting || loadingPriceLevels}
                SelectProps={{
                    multiple: isCreate,
                    renderValue: (selected) => !isCreate ? selected?.displayTitle ?? '' : ''
                }}
            >
                {
                    priceLevels?.map(e => (
                        <MenuItem key={e.id} value={e}>{e.displayTitle}</MenuItem>
                    ))
                }
            </MuiRFF.TextField>
            {
                isCreate &&
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                    {
                        Array.isArray(values.target) && values.target.map((priceLevel) => (
                            <Chips
                                key={priceLevel.id}
                                disabled={loading || submitting || loadingPriceLevels}
                                contentType={ChipsContentType.text}
                                iconPosition={ChipsIconPosition.end}
                                type={ChipsTypes.remove}
                                onIconClick={() => form.change('target', values.target.filter(e => e.id !== priceLevel.id))}
                                content={priceLevel.displayTitle}
                                className={'pl-3'}
                            />
                        ))
                    }
                </Box>
            }
        </>
    )
}

export default UpsertPriceMatrixDialog;
